import { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Side-Drawer (rechts), basierend auf nativem `<dialog>`-Element.
 *
 * Vorteile gegenüber Custom-Modals:
 *  - Browser-eigener Focus-Trap (showModal() macht das)
 *  - ESC-Handler frei Haus
 *  - top-layer Stack vermeidet z-index-Hölle
 *
 * Wir nutzen das auch für den Wartungsfenster-Diff (WT-205 Carry-Over).
 *
 * A11y:
 *  - role wird über `<dialog>` gesetzt
 *  - aria-labelledby zeigt auf den Header
 *  - Backdrop-Klick und ESC schließen den Drawer
 */

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Maximalbreite (Tailwind-Klasse). Default: medium (~640px).
   */
  widthClass?: string;
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  widthClass = 'max-w-2xl',
}: DrawerProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useStableId('drawer-title');
  const descId = useStableId('drawer-desc');

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    const handleClose = (): void => onClose();
    dlg.addEventListener('close', handleClose);
    return () => dlg.removeEventListener('close', handleClose);
  }, [onClose]);

  // ESC schließt (native dialog macht das automatisch via 'close'-Event).
  // Backdrop-Klick: Klick auf das <dialog>-Element selbst (nicht auf Inhalt).
  // ESLint warnt hier (jsx-a11y/no-noninteractive-element-interactions), aber
  // <dialog> ist semantisch korrekt für Modale - wir folgen damit dem
  // bereits bestehenden Pattern in `Dialog.tsx`.
  const onBackdropClick = (event: React.MouseEvent<HTMLDialogElement>): void => {
    if (event.target === ref.current) onClose();
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onClick={onBackdropClick}
      // dialog ist im Top-Layer; wir positionieren via margin-right=0 und full-height
      className={[
        'm-0 ml-auto h-screen p-0 text-text shadow-2xl',
        'rounded-none border-l border-border bg-surface',
        'backdrop:bg-black/50',
        'w-[92vw]',
        widthClass,
      ].join(' ')}
    >
      <div className="flex h-full flex-col">
        <header className="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="truncate text-lg font-semibold">
              {title}
            </h2>
            {description && (
              <div id={descId} className="mt-1 text-sm text-muted">
                {description}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Drawer schließen"
          >
            <X className="size-5" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-2/40 px-6 py-3">
            {footer}
          </footer>
        )}
      </div>
    </dialog>
  );
}

/** Präfix-Wrapper um useId für kollisionsfreie aria-IDs. */
function useStableId(prefix: string): string {
  const id = useId();
  return `${prefix}-${id}`;
}
